import { useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, Users } from "lucide-react";
import { renderTime, dayOffsetBetween } from "@/lib/tripTimes";
import { useDateLocale } from "@/lib/dateLocale";
import { trackEvent } from "@/lib/analytics";
import { DurationDisplay } from "./DurationDisplay";
import { StopsTimeline } from "./StopsTimeline";

interface CityInfo {
  id: number;
  name: string;
  state: string | null;
  /**
   * IANA zone this city's clocks are read in (PR B3). `'UTC'` is the
   * un-backfilled sentinel, not an answer — see @shared/utils/timezoneResolve.
   */
  timezone: string | null;
}

interface IntermediateStop {
  cityId: number;
  cityName: string;
  state: string | null;
  timezone: string | null;
  stopOrder: number;
  /** Minutes of travel from the trip's origin departure; null = unknown/unauthored. */
  travelOffsetMinutes: number | null;
  /** Server-resolved time in this stop's own zone; null = genuinely unknown. */
  timeFormatted: string | null;
  timeIso: string | null;
  timezoneAbbr: string | null;
}

export interface Trip {
  id: number;
  /** The ROUTE's departure instant — the anchor the stops timeline counts from. */
  departureTime: string;
  arrivalTime: string | null;
  /**
   * The PASSENGER's own boarding/alighting for this segment, resolved
   * server-side (PR B3) by the same code that produces the ticket. The client
   * does NOT convert `departureTime` itself: for an intermediate boarder the
   * route departure is a different event, not the same instant in another zone,
   * so no client-side conversion could produce their time.
   *
   * Null = genuinely unknown (an unauthored stop offset, a feeder, or a leg
   * whose occurrence is undecidable). Never a substituted clock.
   */
  boardingIso: string | null;
  boardingTimeFormatted: string | null;
  boardingTimezone: string;
  boardingAbbr: string | null;
  boardingBasis: "passenger" | "route";
  alightingIso: string | null;
  alightingTimeFormatted: string | null;
  alightingTimezone: string;
  alightingAbbr: string | null;
  alightingBasis: "passenger" | "route";
  availableSeats: number;
  segmentId: number;
  segmentPrice: string;
  roundTripPrice: string | null;
  routeName: string;
  durationMinutes: number | null;
  originCity: CityInfo;
  destinationCity: CityInfo;
  intermediateStops: IntermediateStop[];
}

interface TripCardProps {
  trip: Trip;
  passengers: number;
  tripType: "one-way" | "round-trip";
  date: string;
  fromId: string;
  toId: string;
}

/**
 * Editorial trip card.
 * Route-as-timeline: big time on either end, duration + stops badge centered on a hairline rule,
 * price block on the right with brand CTA.
 */
export function TripCard({
  trip,
  passengers,
  tripType,
  date,
  fromId,
  toId,
}: TripCardProps) {
  const { t } = useTranslation();
  const dl = useDateLocale();
  const [, setLocation] = useLocation();
  const [showStops, setShowStops] = useState(false);
  // The server already resolved this passenger's own boarding and alighting, in
  // their own cities' zones. Rendering here is presentation only.
  const departure = trip.boardingIso
    ? renderTime(trip.boardingIso, trip.boardingTimezone, dl.locale)
    : null;
  const departureTime = departure?.time ?? "--:--";
  const departurePeriod = departure?.period ?? "";
  const departureDate = departure?.date ?? "";
  const departureAbbr = trip.boardingAbbr;

  const arrival = trip.alightingIso
    ? renderTime(trip.alightingIso, trip.alightingTimezone, dl.locale)
    : null;
  const arrivalTime = arrival?.time ?? null;
  const arrivalPeriod = arrival?.period ?? null;
  const arrivalDate = arrival?.date ?? null;
  const arrivalAbbr = trip.alightingAbbr;

  // Judged per-zone: "arrives next day" is about boarding on one local date and
  // stepping off on another, which a single-zone comparison can both invent and
  // hide across a border.
  const dayOffset = departure && arrival ? dayOffsetBetween(departure, arrival) : 0;
  const isDifferentDay = dayOffset > 0;

  const basePrice = parseFloat(trip.segmentPrice);
  const roundTripPrice = trip.roundTripPrice
    ? parseFloat(trip.roundTripPrice)
    : null;
  const pricePerPerson =
    tripType === "round-trip" && roundTripPrice ? roundTripPrice : basePrice;
  const totalPrice = pricePerPerson * passengers;

  const hasIntermediateStops = trip.intermediateStops?.length > 0;

  const handleSelect = () => {
    trackEvent("trip_selected", {
      tripId: trip.id,
      routeId: trip.segmentId,
      price: pricePerPerson,
    });

    const params = new URLSearchParams({
      passengers: passengers.toString(),
      segmentId: trip.segmentId.toString(),
      price: pricePerPerson.toString(),
      tripType,
      date,
      from: fromId,
      to: toId,
    });
    setLocation(`/trips/${trip.id}/seats?${params.toString()}`);
  };

  const toggleStops = () => {
    setShowStops((prev) => !prev);
    if (!showStops) trackEvent("trip_stops_expanded", { tripId: trip.id });
  };

  return (
    <article className="bg-white border hairline-strong shadow-sheet hover:shadow-lift transition-shadow">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto] gap-0 lg:divide-x divide-y lg:divide-y-0 divide-hairline">
        {/* Departure */}
        <TimeBlock
          label={t("trips.departure")}
          city={trip.originCity.name}
          time={departureTime}
          period={departurePeriod}
          date={departureDate}
          zoneAbbr={departureAbbr}
        />

        {/* Journey center */}
        <div className="flex flex-row lg:flex-col items-center justify-center px-5 md:px-8 py-5 lg:py-6 gap-4 lg:gap-3 bg-stone-50/50">
          <DurationDisplay
            durationMinutes={trip.durationMinutes}
            className="font-display text-[1rem] md:text-[1.05rem] text-ink tabular"
          />
          <RouteLine />
          {hasIntermediateStops ? (
            <button
              onClick={toggleStops}
              className="text-eyebrow uppercase text-ink-muted hover:text-ink inline-flex items-center gap-1 focus-visible-ring"
              aria-expanded={showStops}
              aria-controls={`stops-${trip.id}`}
            >
              {showStops ? t("trips.hideStops") : t("trips.showStops")}
              {showStops ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
          ) : (
            <span className="text-eyebrow uppercase text-ink-subtle">
              {t("trips.direct", "Direct")}
            </span>
          )}
        </div>

        {/* Arrival */}
        <TimeBlock
          label={t("trips.arrival")}
          city={trip.destinationCity.name}
          time={arrivalTime || "--:--"}
          period={arrivalPeriod || ""}
          date={isDifferentDay ? arrivalDate || "" : ""}
          dateBadge={
            isDifferentDay
              ? dayOffset > 1
                ? t("trips.plusDays", "+{{count}} days", { count: dayOffset })
                : t("trips.nextDay", "+1 day")
              : undefined
          }
          zoneAbbr={arrivalAbbr}
        />

        {/* Price + CTA */}
        <div className="flex flex-col justify-between items-stretch p-5 md:p-6 lg:min-w-[16rem]">
          <div>
            <div className="text-eyebrow uppercase text-ink-subtle">
              {tripType === "round-trip"
                ? t("trips.roundTripTotal", "Round trip total")
                : t("trips.total", "Total")}
            </div>
            <div className="font-display text-[2rem] lg:text-[2.25rem] text-ink tabular leading-none mt-1">
              ${totalPrice.toFixed(0)}
              <span className="text-[0.8rem] text-ink-muted ml-1.5 tracking-wide">
                USD
              </span>
            </div>
            {passengers > 1 && (
              <div className="text-xs text-ink-subtle tabular mt-1">
                ${pricePerPerson.toFixed(0)} × {passengers}
              </div>
            )}
            <div className="flex items-center gap-1.5 text-xs text-ink-muted mt-3">
              <Users className="h-3 w-3" aria-hidden="true" />
              <span className="tabular">
                {trip.availableSeats} {t("trips.seatsLabel")}
              </span>
            </div>
          </div>
          <button
            onClick={handleSelect}
            className="mt-5 w-full h-11 bg-brand text-brand-ink font-semibold text-[0.85rem] uppercase tracking-[0.12em] flex items-center justify-center gap-2 hover:bg-ink transition-[background] focus-visible-ring"
            data-testid={`trip-select-${trip.id}`}
            data-available-seats={trip.availableSeats}
          >
            {t("trips.selectTrip")}
          </button>
        </div>
      </div>

      {hasIntermediateStops && (
        <div
          id={`stops-${trip.id}`}
          className={`overflow-hidden transition-all duration-300 ease-in-out motion-reduce:transition-none ${
            showStops ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-5 md:px-8 py-5 border-t hairline bg-stone-50">
            <p className="text-eyebrow uppercase text-ink-subtle mb-3">
              {t("trips.intermediateStops")}
            </p>
            <StopsTimeline
              stops={trip.intermediateStops}
              tripDepartureTime={trip.departureTime}
              originTimezone={trip.boardingTimezone}
              destinationTimezone={trip.alightingTimezone}
            />
          </div>
        </div>
      )}
    </article>
  );
}

function TimeBlock({
  label,
  city,
  time,
  period,
  date,
  dateBadge,
  zoneAbbr,
}: {
  label: string;
  city: string;
  time: string;
  period: string;
  date: string;
  dateBadge?: string;
  /**
   * Zone abbreviation ("CST"/"CDT"), shown only when this journey crosses
   * zones. On a same-zone trip it is null and nothing renders — a passenger
   * does not need telling which clock when there is only one.
   */
  zoneAbbr?: string | null;
}) {
  return (
    <div className="flex flex-col justify-center p-5 md:p-6 min-w-0">
      <span className="text-eyebrow uppercase text-ink-subtle">{label}</span>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="platform-board text-[2.25rem] md:text-[2.75rem] text-ink tabular leading-none">
          {time}
        </span>
        <span className="text-xs text-ink-muted uppercase tabular">
          {period}
        </span>
        {zoneAbbr && (
          <span className="text-xs text-ink-subtle uppercase tabular">
            {zoneAbbr}
          </span>
        )}
        {dateBadge && (
          <span className="text-eyebrow uppercase text-brand border border-brand px-1.5 py-0.5 ml-1 tabular">
            {dateBadge}
          </span>
        )}
      </div>
      <div className="mt-2 text-[0.95rem] text-ink font-medium truncate">
        {city}
      </div>
      {date && (
        <div className="text-xs text-ink-subtle mt-0.5 tabular">{date}</div>
      )}
    </div>
  );
}

function RouteLine() {
  return (
    <div
      className="flex items-center gap-0 flex-1 lg:flex-initial lg:w-full min-w-[4rem] lg:min-w-[6rem]"
      aria-hidden="true"
    >
      <span className="h-2 w-2 bg-ink rounded-full flex-shrink-0" />
      <span className="flex-1 h-px bg-hairline-strong" />
      <span className="h-2 w-2 bg-brand rounded-full flex-shrink-0" />
    </div>
  );
}
